import { useContext, useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { PostsContext } from "../../context/PostsContext";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  WhatsappShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappIcon,
} from "react-share";
import YoastSeo from "../common/YoastSeo";

const WP_URL = "https://parmazing.net";

const PostDisplay = () => {
  const { slug } = useParams();
  const { posts, loading } = useContext(PostsContext);

  const [post, setPost] = useState(null);
  const [prevPost, setPrevPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [categories, setCategories] = useState({});
  const [featuredImage, setFeaturedImage] = useState(null); // { src, alt, width, height }
  const [contentFirstImage, setContentFirstImage] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  // fetch category names once posts are loaded
  useEffect(() => {
    if (!loading && posts.length > 0) {
      const categoryIds = [
        ...new Set(posts.flatMap((p) => p.categories || [])),
      ];
      if (categoryIds.length === 0) return;
      fetch(`${WP_URL}/wp-json/wp/v2/categories?include=${categoryIds.join(",")}`)
        .then((res) => res.json())
        .then((data) => {
          const categoryMap = {};
          data.forEach((category) => {
            categoryMap[category.id] = category.name;
          });
          setCategories(categoryMap);
        })
        .catch((err) => {
          console.error("Failed to fetch categories:", err);
        });
    }
  }, [loading, posts]);

  const getCategoryNames = useCallback(
    (p) => {
      if (p.categories && Array.isArray(p.categories)) {
        return p.categories.map((id) => categories[id]).filter(Boolean);
      }
      return [];
    },
    [categories]
  );

  // pick current post, prev/next, and related posts
  useEffect(() => {
    if (!loading && Object.keys(categories).length > 0) {
      const index = posts.findIndex((p) => p.slug === slug);
      if (index !== -1) {
        const currentPost = posts[index];
        setPost(currentPost);
        setPrevPost(posts[index - 1] || null);
        setNextPost(posts[index + 1] || null);

        const excludedCategories = ["Paintings", "Quiz", "Poll"];
        const otherPosts = posts.filter((p) => {
          if (p.slug === slug) return false;
          const categoryNames = getCategoryNames(p);
          return !categoryNames.some((name) =>
            excludedCategories.includes(name)
          );
        });

        const shuffled = otherPosts.sort(() => 0.5 - Math.random());
        setRelatedPosts(shuffled.slice(0, 3));
      } else {
        setPost(null);
        setPrevPost(null);
        setNextPost(null);
        setRelatedPosts([]);
      }
    }
  }, [loading, slug, posts, categories, getCategoryNames]);

  // Extract featured media + fallback first image in content.
  useEffect(() => {
    setFeaturedImage(null);
    setContentFirstImage(null);

    if (!post) return;

    const fetchFeaturedMedia = async (mediaId) => {
      if (!mediaId || mediaId === 0) return null;
      try {
        const res = await fetch(`${WP_URL}/wp-json/wp/v2/media/${mediaId}`);
        if (!res.ok) return null;
        const data = await res.json();
        let src = data.source_url || "";
        let width = null;
        let height = null;

        if (data.media_details && data.media_details.sizes) {
          const sizes = data.media_details.sizes;
          const pick =
            sizes.large || sizes.medium || sizes.full || data.media_details;
          if (pick && pick.source_url) src = pick.source_url;
          if (pick && pick.width) width = pick.width;
          if (pick && pick.height) height = pick.height;
        } else if (data.media_details && data.media_details.width) {
          width = data.media_details.width;
          height = data.media_details.height;
        }

        return {
          src: String(src || ""),
          alt: String(data.alt_text || data.title?.rendered || ""),
          width: width ? String(width) : "",
          height: height ? String(height) : "",
        };
      } catch (e) {
        console.error("Failed to fetch featured media", e);
        return null;
      }
    };

    const findFirstImageInContent = (html) => {
      if (!html) return null;
      const match = html.match(/<img [^>]*src=["']([^"']+)["'][^>]*>/i);
      if (match && match[1]) {
        return { src: String(match[1]), alt: "" };
      }
      return null;
    };

    (async () => {
      if (
        post._embedded &&
        post._embedded["wp:featuredmedia"] &&
        post._embedded["wp:featuredmedia"][0]
      ) {
        const fm = post._embedded["wp:featuredmedia"][0];
        let src = fm.source_url || "";
        let width = "";
        let height = "";
        if (fm.media_details && fm.media_details.sizes) {
          const sizes = fm.media_details.sizes;
          const pick = sizes.large || sizes.medium || sizes.full || fm.media_details;
          src = pick.source_url || src;
          width = pick.width ? String(pick.width) : width;
          height = pick.height ? String(pick.height) : height;
        } else if (fm.media_details && fm.media_details.width) {
          width = String(fm.media_details.width);
          height = String(fm.media_details.height);
        }
        setFeaturedImage({
          src: String(src || ""),
          alt: String(fm.alt_text || fm.title?.rendered || ""),
          width: width || "",
          height: height || "",
        });
      } else if (post.featured_media) {
        const fm = await fetchFeaturedMedia(post.featured_media);
        if (fm && fm.src) setFeaturedImage(fm);
      }

      const firstImg = findFirstImageInContent(post.content?.rendered || "");
      if (firstImg) setContentFirstImage(firstImg);
    })();
  }, [post]);

  // styling post content images after render
  useEffect(() => {
    if (!post) return;
    const timer = setTimeout(() => {
      const contentDiv = document.querySelector(".post-content");
      if (!contentDiv) return;

      const images = contentDiv.querySelectorAll("img");
      images.forEach((img) => {
        img.style.display = "block";
        img.style.margin = "0 auto";
        img.style.float = "none";
        img.style.maxWidth = window.innerWidth < 768 ? "100%" : "80%";
        img.style.width = "auto";
        img.style.height = "auto";
        img.style.borderRadius = "12px";
        img.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
      });

      const figures = contentDiv.querySelectorAll(
        "figure, .wp-block-image, div[id^='attachment_'], .wp-caption"
      );
      figures.forEach((fig) => {
        fig.style.textAlign = "center";
        fig.style.margin = "1rem auto";
        fig.style.float = "none";
        fig.style.display = "block";
        fig.style.maxWidth = window.innerWidth < 768 ? "100%" : "80%";
      });

      const alignedElements = contentDiv.querySelectorAll(
        ".alignleft, .alignright, .aligncenter"
      );
      alignedElements.forEach((el) => {
        el.style.float = "none";
        el.style.textAlign = "center";
        el.style.margin = "1rem auto";
        el.style.display = "block";
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [post]);

  const handleNavClick = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  };

  if (loading || !categories || !Object.keys(categories).length) {
    return (
      <div className="flex justify-center items-center min-h-screen pt-24">
        <p className="text-yellow-600 font-semibold animate-pulse text-lg">
          Loading...
        </p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-center px-6">
        <div className="bg-red-100 border border-red-400 text-red-700 p-6 rounded-lg shadow-md w-full max-w-lg">
          <h2 className="text-2xl font-bold">Post Not Found</h2>
          <p className="text-gray-600 mt-2">
            The post you're looking for doesn't exist or may have been removed.
          </p>
          <Link
            to="/blog"
            className="mt-4 inline-block text-yellow-600 font-semibold hover:text-yellow-700 transition"
          >
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // prefer WP canonical link so scrapers hit the server-rendered WP page
  const shareUrl = String(post?.link || window.location.href || "");
  const title = String((post?.title?.rendered || "Parmazing").replace(/<[^>]*>/g, ""));
  const heroImage = String(featuredImage?.src || contentFirstImage?.src || "");
  const heroAlt = String(featuredImage?.alt || contentFirstImage?.alt || title);

  const mainDateStr = new Date(post.date).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const estimateReadTime = (p) => {
    const txt = String(p?.content?.rendered || "").replace(/<[^>]*>/g, "");
    const words = txt.trim().split(/\s+/).filter(Boolean).length;
    const mins = Math.max(1, Math.ceil(words / 200));
    return `${mins} min${mins > 1 ? "s" : ""} read`;
  };
  const readTime = estimateReadTime(post);

  return (
    <div className="relative min-h-screen flex flex-col items-center px-2 sm:px-4 md:px-6 lg:px-8 pt-20 sm:pt-24 md:pt-28 pb-12 sm:pb-16 md:pb-20 overflow-hidden bg-gray-50">
      {/* Yoast-driven meta tags. Pass post as fallback so the component can use it when yoast data is missing */}
      <YoastSeo wpUrl={WP_URL} endpoint="posts" slug={slug} post={post} fallback={{
        title,
        description: post?.excerpt?.rendered?.replace(/<[^>]*>/g, "")?.trim()?.slice(0,160),
        image: heroImage,
        canonical: shareUrl
      }} />

      <div className="relative z-10 w-full max-w-6xl">
        {/* HERO: split layout */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="flex flex-col md:flex-row items-stretch">
            {/* left: image */}
            <div className="w-full md:w-1/2 h-64 md:h-[28rem] overflow-hidden relative">
              {heroImage ? (
                <>
                  <img
                    src={heroImage}
                    alt={heroAlt || post.title?.rendered || "Featured image"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent md:hidden" />
                </>
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
                  No featured image
                </div>
              )}
            </div>

            {/* right: text */}
            <div className="w-full md:w-1/2 p-6 flex flex-col justify-center gap-4">
              <div>
                <h1
                  className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight break-words"
                  dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                />
                <div className="w-14 sm:w-20 h-1 bg-yellow-500 mt-3 rounded-full" />
              </div>

              <div className="text-sm text-gray-500 flex flex-wrap items-center gap-3">
                <span>{mainDateStr}</span>
                <span>•</span>
                <span>{readTime}</span>
                {getCategoryNames(post).length > 0 && (
                  <>
                    <span>•</span>
                    <span className="text-sm text-gray-700">{getCategoryNames(post).join(", ")}</span>
                  </>
                )}
              </div>

              {post.excerpt?.rendered && (
                <div
                  className="text-gray-700 text-sm mt-2 line-clamp-3"
                  dangerouslySetInnerHTML={{ __html: post.excerpt.rendered.replace(/<[^>]*>/g, "") }}
                />
              )}
            </div>
          </div>

          {/* share row */}
          <div className="hidden md:flex items-center justify-between px-6 py-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              {/* share canonical WP permalink so scrapers fetch WP page with OG tags */}
              <FacebookShareButton url={shareUrl} quote={title}>
                <FacebookIcon size={28} round />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={title}>
                <TwitterIcon size={28} round />
              </TwitterShareButton>
              <LinkedinShareButton url={post.link} title={title}>
                <LinkedinIcon size={28} round />
              </LinkedinShareButton>
              <WhatsappShareButton url={shareUrl} title={title}>
                <WhatsappIcon size={28} round />
              </WhatsappShareButton>
            </div>
            <Link to="/blog" className="inline-block text-yellow-600 font-medium hover:text-yellow-700 transition text-sm">← Back to Blog</Link>
          </div>
        </div>

        {/* ARTICLE */}
        <article className="mt-8 px-0 md:px-4">
          <div
            className="post-content prose prose-lg mx-auto w-full max-w-5xl text-gray-700
              prose-a:text-yellow-600 prose-a:underline hover:prose-a:text-yellow-700
              prose-blockquote:border-l-4 prose-blockquote:border-yellow-500 prose-blockquote:pl-4 prose-blockquote:text-gray-600"
            dangerouslySetInnerHTML={{ __html: post.content.rendered }}
          />
        </article>

        {/* Prev/Next nav */}
        <div className="px-0 md:px-6 pb-8">
          <div className="mt-10 flex flex-col sm:flex-row sm:justify-between gap-3 w-full">
            {prevPost ? (
              <Link to={`/blog/${prevPost.slug}`} onClick={handleNavClick} className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-gray-100 hover:bg-yellow-50 transition text-sm w-full sm:w-auto sm:max-w-[250px] group">
                <span className="text-lg group-hover:-translate-x-1 transition-transform">←</span>
                <div className="flex flex-col text-left min-w-0">
                  <span className="truncate font-medium hover:text-yellow-600 transition" dangerouslySetInnerHTML={{ __html: prevPost.title.rendered }} />
                </div>
              </Link>
            ) : (
              <div className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-gray-100 text-gray-400 text-sm w-full sm:w-auto sm:max-w-[250px]">← No Previous Post</div>
            )}

            {nextPost ? (
              <Link to={`/blog/${nextPost.slug}`} onClick={handleNavClick} className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-gray-100 hover:bg-yellow-50 transition text-sm w-full sm:w-auto sm:max-w-[250px] sm:ml-auto group">
                <div className="flex flex-col text-right min-w-0">
                  <span className="truncate font-medium hover:text-yellow-600 transition" dangerouslySetInnerHTML={{ __html: nextPost.title.rendered }} />
                </div>
                <span className="text-lg group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            ) : (
              <div className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-gray-100 text-gray-400 text-sm w-full sm:w-auto sm:max-w-[250px] sm:ml-auto">No Next Post →</div>
            )}
          </div>

          <div className="mt-8 text-center">
            <Link to="/blog" className="px-6 py-3 text-white bg-yellow-500 hover:bg-yellow-600 font-medium rounded-full shadow-md transition-all transform hover:scale-105 text-sm">← Back to Blog</Link>
          </div>
        </div>

        {/* RELATED */}
        {relatedPosts.length > 0 && (
          <section className="relative z-10 w-full max-w-6xl mt-8">
            <div className="bg-white shadow-lg rounded-2xl p-4 sm:p-6 md:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">Related Posts</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {relatedPosts.map((relatedPost) => {
                  const relatedDateStr = new Date(relatedPost.date).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                  return (
                    <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`} onClick={handleNavClick} className="group bg-gray-50 rounded-xl p-3 sm:p-4 hover:bg-yellow-50 hover:shadow-md transition-all duration-200 border border-transparent hover:border-yellow-200">
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 group-hover:text-yellow-700 transition-colors line-clamp-2 mb-2 leading-tight" dangerouslySetInnerHTML={{ __html: relatedPost.title.rendered }} />
                      <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">{relatedDateStr}</p>
                      {relatedPost.excerpt && (
                        <div className="text-xs sm:text-sm text-gray-600 line-clamp-2 sm:line-clamp-3 mb-2" dangerouslySetInnerHTML={{ __html: relatedPost.excerpt.rendered.replace(/<[^>]*>/g, "") }} />
                      )}
                      <div className="text-yellow-600 text-xs sm:text-sm font-medium group-hover:text-yellow-700 transition-colors">Read more →</div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PostDisplay;
